import * as Yup from 'yup'
import { isEmpty, isNil, omit, some } from 'lodash'

import { carriedWeight } from '@/views/Organizations/components/connectionWeight.helpers'
import { ORG_NEW_FILES_PAUSED_LABEL } from '@/views/Organizations/components/OrgSyncPausedBanner'

// Mirror the agent-gpt server-side validators for fast feedback; the server
// stays the source of truth. Drive ids are opaque case-sensitive tokens.
const DRIVE_ITEM_ID_RE = /^[a-zA-Z0-9_-]{10,}$/

// Operators paste links straight from the Drive UI; Shared Drive roots and
// folders share the /folders/<id> URL shape, so one extractor covers both
// fields. A bare id passes through unchanged. The server rejects raw URLs, so
// extraction happens here (validation and submit both go through it).
const DRIVE_URL_RE = /drive\.google\.com\/(?:drive\/)?(?:u\/\d+\/)?folders\/([a-z0-9_-]{10,})/i

export const extractDriveItemId = (value) => {
  const trimmed = (value || '').trim()
  const match = trimmed.match(DRIVE_URL_RE)
  return isNil(match) ? trimmed : match[1]
}

const isValidDriveItemId = (value) => DRIVE_ITEM_ID_RE.test(extractDriveItemId(value))

// Every row must name a drive (Folder stays optional), and at least one drive
// is required — an empty allowlist is not allowed.
export const ALLOWLIST_ROW_SCHEMA = Yup.object({
  drive_id: Yup.string()
    .required('Drive is required')
    .test(
      'drive-id',
      'Paste the Shared Drive link or its id',
      (value) => isEmpty(value) || isValidDriveItemId(value)
    ),
  folder_id: Yup.string().test(
    'folder-id',
    'Paste the folder link or its id',
    (value) => isEmpty((value || '').trim()) || isValidDriveItemId(value)
  ),
})

export const ALLOWLIST_SCHEMA = Yup.array()
  .of(ALLOWLIST_ROW_SCHEMA)
  .min(1, 'Add at least one drive')

// `config_cids` empty = answers every playbook (QCER-1075), matching the
// upload form's "no playbooks selected" default.
export const BLANK_ALLOWLIST_ROW = {
  drive_id: '',
  folder_id: '',
  config_cids: [],
}

export const toFormAllowlist = (allowlist) =>
  isEmpty(allowlist)
    ? [{ ...BLANK_ALLOWLIST_ROW }]
    : allowlist.map((entry) => ({
        drive_id: entry.drive_id || '',
        folder_id: entry.folder_id || '',
        // Always an array so the multi-select never sees undefined; the server
        // omits the key entirely on an unscoped entry.
        config_cids: entry.config_cids || [],
        // No column edits it; carried so Save writes the stored weight back.
        ...carriedWeight(entry),
      }))

// The API stores whole-drive entries with the folder key absent (one spelling,
// not two), and only ever bare ids — pasted links normalize here on submit.
export const toApiAllowlist = (rows) =>
  rows.map((row) => {
    const driveId = extractDriveItemId(row.drive_id)
    const folderId = extractDriveItemId(row.folder_id)
    const wholeDrive = isEmpty(folderId)
    // Omitted rather than sent as [] when nothing is selected, so an untouched
    // allowlist round-trips byte-identical and needs no migration (QCER-1075).
    // Omitted on a folder row whatever it holds: scope applies per drive and
    // the server refuses it there (QCER-1123) — a row saved with one before
    // that rule would otherwise make every later save of the connection 422,
    // from a control the operator cannot even open.
    const scope = wholeDrive && !isEmpty(row.config_cids) ? { config_cids: row.config_cids } : {}
    const carried = { ...scope, ...carriedWeight(row) }
    return wholeDrive
      ? { drive_id: driveId, ...carried }
      : { drive_id: driveId, folder_id: folderId, ...carried }
  })

// Entry identity, mirrored from agent-gpt's google_drive_removal service:
// EXACT CASE (Drive ids are case-sensitive opaque tokens — folding two
// distinct ids into one identity would merge their entries), and a blank
// folder means the whole drive; there is no '*' spelling.
export const normalizedFolder = (folderId) => (folderId || '').trim()

export const isWholeDrive = (folderId) => isEmpty(normalizedFolder(folderId))

// The stored entries, minus a scope no folder entry can carry. Scope applies
// per drive and the server refuses it on a folder entry (QCER-1123), so one
// saved before that rule must not be re-sent by any path that PATCHes the
// stored list back — `toApiAllowlist` covers the form's Save; this covers a
// replace, which sends the stored entries as they are. Otherwise every replace
// on the connection would 422 from a control the operator cannot even open.
export const withoutFolderScopes = (allowlist) =>
  allowlist.map((entry) => (isWholeDrive(entry.folder_id) ? entry : omit(entry, 'config_cids')))

export const driveIdOf = (record) => (record.drive_id || '').trim()

export const sameEntry = (entryA, entryB) =>
  driveIdOf(entryA) === driveIdOf(entryB) &&
  normalizedFolder(entryA.folder_id) === normalizedFolder(entryB.folder_id)

// What a removal would do, for the confirmation body. The server is the
// authority; this only shapes the text. Weaviate nodes carry only the drive
// id (no folder identity), so documents are purged immediately ONLY when the
// removal leaves no allowlist entry for the drive — otherwise the server
// defers the cleanup to the next nightly reconcile. document_count is
// likewise DRIVE-scoped (the drive's rows all repeat one holdings number),
// so it is surfaced once, never as a per-row sum.
export const removalImpact = (syncState, persistedAllowlist, driveId, folderId) => {
  const removal = { drive_id: driveId, folder_id: folderId }
  const remaining = persistedAllowlist.filter((entry) => !sameEntry(entry, removal))
  const purgeDeferred = remaining.some((entry) => driveIdOf(entry) === driveId)
  if (purgeDeferred || isNil(syncState) || isEmpty(syncState.rows)) {
    return { purgeDeferred, driveDocumentCount: null }
  }
  const driveRow = syncState.rows.find(
    (row) => row.kind !== 'selector' && driveIdOf(row) === driveId && !isNil(row.document_count)
  )
  return {
    purgeDeferred,
    driveDocumentCount: isNil(driveRow) ? null : driveRow.document_count,
  }
}

// The persisted-row remove label must not overstate: removal purges
// immediately only when it de-scopes the drive; otherwise it just retires the
// entry (cleanup owned by the nightly reconcile). Reuses removalImpact so the
// label and the confirmation dialog can never disagree. Sync state is
// irrelevant to the deferred/immediate branch, hence the null.
export const persistedRemoveLabel = (persistedAllowlist, row) =>
  removalImpact(null, persistedAllowlist, driveIdOf(row), row.folder_id).purgeDeferred
    ? 'Remove allowlist entry'
    : 'Remove and purge documents'

export const FOLDER_NOT_FOUND_ERROR = 'folder_not_found'
export const ACCESS_DENIED_ERROR = 'access_denied'

// Operator-readable labels for the flow's low-cardinality error reasons; an
// unknown reason falls back to the raw code so new backend reasons still show.
// The chip's label sets the Status column's width, so it stays a couple of
// words; the sentence that explains what to do about it rides in the tooltip.
export const SYNC_ERROR_LABELS = {
  [ACCESS_DENIED_ERROR]: {
    label: 'Access denied',
    description:
      'The Shared Drive is not shared with the Balto service account — add it as Viewer ' +
      'under Manage members.',
  },
  drive_error: {
    label: 'Drive API error',
    description: 'The Drive API rejected the request — check the drive id.',
  },
  [FOLDER_NOT_FOUND_ERROR]: {
    label: 'Folder not found',
    description: 'The configured folder is not in this drive (deleted, moved, or a typo).',
  },
  cap_exceeded: {
    label: 'Cap exceeded',
    description:
      // The sync lists the whole shared drive even for a folder row, so picking a folder cannot help.
      'Stopped: this shared drive has more than 50,000 files and folders, which is more than ' +
      'Balto syncs, even when only one folder is selected. Move the content you need into a ' +
      'smaller shared drive, or contact Balto support.',
  },
  mass_deletion_guard: {
    label: 'Deletion guard',
    description:
      'Aborted — the run would have deleted most of this drive, which usually means a bad ' +
      'or empty enumeration rather than a real deletion.',
  },
  items_failed: {
    label: 'Items failed',
    description: 'Some items failed to ingest; the next run retries them.',
  },
  manifest_write_failed: {
    label: 'File list not saved',
    description:
      "Couldn't record this run's file list, so the run stopped there. The next run retries.",
  },
  manifest_unavailable: {
    label: 'File list unavailable',
    description:
      "Couldn't read the file list or the block list, so the run stopped there. The next run retries.",
  },
  // RTG3-1300: new files left out because the org is at its object ceiling.
  over_ceiling: {
    label: ORG_NEW_FILES_PAUSED_LABEL,
    description:
      'Some new files weren’t added: this organization’s knowledge base was at its size ' +
      'limit when this last synced. Changes and deletions still sync, and the files are ' +
      'added once it’s back under.',
  },
  unexpected: {
    label: 'Failed',
    description: 'Unexpected failure. Contact Balto support if this persists.',
  },
}

// Docs Hub page; it redirects to login when signed out.
export const SHARED_DRIVE_ACCESS_GUIDE_URL =
  'https://docs.balto.ai/docs/connecting-google-drive-to-balto-ai-answers'

export const syncRowFolderLabel = (row) => (isEmpty(row.folder_id) ? 'Whole drive' : row.folder_id)

/**
 * The sync rows one persisted allowlist entry answers for. Google Drive sync
 * rows are one per allowlist entry, matched on exact-case drive id plus
 * normalized folder (blank = whole drive), so coverage is at most one row and
 * the drive-scoped document_count is never summed across entries.
 * `persistedEntries` is unused but kept for signature parity with SharePoint.
 */
export const googleDriveSyncRowsFor = (entry, persistedEntries, syncRows) =>
  syncRows.filter(
    (row) =>
      driveIdOf(row) === driveIdOf(entry) &&
      normalizedFolder(row.folder_id) === normalizedFolder(entry.folder_id)
  )

// Sync rows carry no drive name, so a whole-drive root is labelled by its id.
export const driveRootLabel = (driveId) => `Drive ${driveId}`

/**
 * Whether the sync is blocked on Balto not being able to read a Shared Drive yet.
 * Rows for a drive no longer in the allowlist are ignored; they linger until the next run.
 * @param {Object} syncState undefined | null | the connection's ingest state
 * @param {Object[]} persistedAllowlist the connection's saved allowlist entries
 * @returns {boolean} true when a configured drive's row failed with access_denied
 */
export const awaitingDriveAccess = (syncState, persistedAllowlist) =>
  !isNil(syncState?.rows) &&
  some(persistedAllowlist, (entry) =>
    some(
      googleDriveSyncRowsFor(entry, persistedAllowlist, syncState.rows),
      (row) => row.last_error === ACCESS_DENIED_ERROR
    )
  )

/**
 * The Manage files roots for a row's covered sync rows: its drive, or its folder in that drive.
 * Sync rows carry no drive name, so a root is labelled by its id; the modal renames a folder
 * from its first page. A folder the sync cannot find has nothing to list.
 * @returns {ManageFilesRoot[]} typedef in ManageFilesModal.helpers.js
 */
export const googleDriveManageFilesRoots = (covered) =>
  covered
    .filter((row) => row.kind === 'drive' && row.last_error !== FOLDER_NOT_FOUND_ERROR)
    .map((row) => {
      const wholeDrive = isWholeDrive(row.folder_id)
      return {
        driveId: row.drive_id,
        rootId: wholeDrive ? null : normalizedFolder(row.folder_id),
        label: wholeDrive ? driveRootLabel(row.drive_id) : `Folder ${row.folder_id}`,
        lastSyncedAt: row.last_synced_at,
      }
    })
